// @flow
import * as React from 'react';
import { useState, useEffect } from 'react';

type Props = {
  isOpen: boolean,
  onAccept: () => void,
  showCloseButton?: boolean,
  onClose?: () => void,
  viewMode?: boolean,
};

const TermsAndConditionsModal = ({
  isOpen,
  onAccept,
  showCloseButton = false,
  onClose,
  viewMode = false
}: Props) => {

  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  // viewMode表示只是查看条款（底栏点击），非viewMode表示需要接受条款（首页弹框）
  const isViewMode = viewMode === true;
  console.log('T&C Modal: isViewMode:', isViewMode);

  useEffect(() => {
    console.log('T&C Modal: useEffect triggered', { isOpen, viewMode });
    if (isOpen) {
      console.log('T&C Modal: Modal is opening');
      // 只有在内容为空且不在加载中时才获取内容
      if (!content && !loading) {
        fetchContent();
      }
      setIsConsentChecked(false);

      // 对于接受模式，完全禁用页面交互
      if (!viewMode) {
        // 禁用body滚动
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = '0';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.bottom = '0';
        document.body.style.userSelect = 'none';
        document.body.style.pointerEvents = 'none';
        document.body.setAttribute('data-modal-active', 'true');

        // 阻止键盘事件
        window.modalKeyHandler = (e) => {
          if (e.key === 'Tab') {
            // 允许Tab键在模态框内导航
            const modal = document.querySelector('.terms-modal-container.acceptance-mode');
            if (modal && !modal.contains(e.target)) {
              e.preventDefault();
              e.stopPropagation();
            }
          } else if (e.key === 'Escape') {
            // 阻止Escape键关闭
            e.preventDefault();
            e.stopPropagation();
          }
        };

        document.addEventListener('keydown', window.modalKeyHandler, true);
        document.addEventListener('keyup', window.modalKeyHandler, true);
        document.addEventListener('keypress', window.modalKeyHandler, true);

        console.log('T&C Modal: All interactions blocked for acceptance mode');
      }
    } else {
      console.log('T&C Modal: Modal is closing');
      // 恢复页面交互
      if (document.body.hasAttribute('data-modal-active')) {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.bottom = '';
        document.body.style.userSelect = '';
        document.body.style.pointerEvents = '';
        document.body.removeAttribute('data-modal-active');

        // 移除事件监听器
        if (window.modalKeyHandler) {
          document.removeEventListener('keydown', window.modalKeyHandler, true);
          document.removeEventListener('keyup', window.modalKeyHandler, true);
          document.removeEventListener('keypress', window.modalKeyHandler, true);
          window.modalKeyHandler = null;
        }

        console.log('T&C Modal: Page interactions restored');
      }
    }

    return () => {
      // 组件卸载时确保恢复正常状态
      if (document.body.hasAttribute('data-modal-active')) {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.bottom = '';
        document.body.style.userSelect = '';
        document.body.style.pointerEvents = '';
        document.body.removeAttribute('data-modal-active');

        // 移除事件监听器
        if (window.modalKeyHandler) {
          document.removeEventListener('keydown', window.modalKeyHandler, true);
          document.removeEventListener('keyup', window.modalKeyHandler, true);
          document.removeEventListener('keypress', window.modalKeyHandler, true);
          window.modalKeyHandler = null;
        }
        console.log('T&C Modal: All interactions restored on cleanup');
      }
    };
  }, [isOpen, viewMode]);

  // 同步复选框状态到DOM
  useEffect(() => {
    if (isOpen && !viewMode) {
      // 查找并同步配置文件中的复选框状态
      const checkbox = document.querySelector('.terms-checkbox input[type="checkbox"]');
      if (checkbox) {
        checkbox.checked = isConsentChecked;
      }

      // 同步接受按钮的启用状态
      const acceptBtn = document.querySelector('.terms-accept-btn');
      if (acceptBtn) {
        acceptBtn.disabled = accepting || !isConsentChecked;
        // 动态设置按钮文案
        const acceptText = accepting
          ? content?.accepting_button_text || 'Accepting...'
          : content?.accept_button_text || '同意して続ける';
        acceptBtn.textContent = acceptText;
      }
    }
  }, [isConsentChecked, accepting, isOpen, viewMode]);



  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await fetch('/terms_and_conditions/content');
      const data = await response.json();
      console.log('T&C Modal: Fetched content data:', data);
      console.log('T&C Modal: details_button_url:', data.details_button_url);
      setContent(data);
    } catch (error) {
      console.error('Failed to fetch terms:', error);
      // 使用默认内容
      setContent({
        title: "eBaymag 利用規約および日本向け補足規定の更新について",
        content: "<p>eBaymagをご利用いただくにあたり、以下の規約をご確認ください。</p>"
      });
    } finally {
      setLoading(false);
    }
  };

  const getCSRFToken = () => {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : '';
  };

  const handleAccept = async () => {
    if (!isConsentChecked) {
      return;
    }

    try {
      setAccepting(true);
      const response = await fetch('/terms_and_conditions/accept', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': getCSRFToken()
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      window.dispatchEvent(new CustomEvent('termsAccepted'));
      onAccept();
    } catch (error) {
      console.error('Failed to accept terms:', error);
      alert('Failed to accept terms. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  // 处理配置文件中HTML内容的点击事件
  const handleContentClick = (e) => {
    // 处理复选框点击
    if (e.target.type === 'checkbox') {
      setIsConsentChecked(e.target.checked);
      console.log('T&C Modal: Checkbox changed:', e.target.checked);
      return;
    }

    // 处理接受按钮点击
    if (e.target.classList.contains('terms-accept-btn')) {
      e.preventDefault();
      console.log('T&C Modal: Accept button clicked, consent:', isConsentChecked);
      handleAccept();
      return;
    }

    // 处理详细按钮点击
    if (e.target.classList.contains('terms-details-btn')) {
      e.preventDefault();
      console.log('T&C Modal: Details button clicked');
      // 动态打开详情链接
      const url = content?.details_button_url || 'https://www.ebay.co.jp/ebaymag/terms';
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // 过滤view mode下的HTML内容，移除复选框和按钮
  const getFilteredContent = (htmlContent) => {
    if (!isViewMode || !htmlContent) {
      return htmlContent;
    }

    // 创建一个临时DOM元素来解析和过滤HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // 移除复选框容器
    const checkboxElements = tempDiv.querySelectorAll('.terms-checkbox');
    checkboxElements.forEach(el => el.remove());

    // 移除footer按钮容器
    const footerElements = tempDiv.querySelectorAll('.terms-footer');
    footerElements.forEach(el => el.remove());

    // 移除单独的按钮
    const buttons = tempDiv.querySelectorAll('.terms-accept-btn, .terms-details-btn, button');
    buttons.forEach(el => el.remove());

    // 移除复选框input
    const checkboxInputs = tempDiv.querySelectorAll('input[type="checkbox"]');
    checkboxInputs.forEach(el => el.remove());

    return tempDiv.innerHTML;
  };

  // 判断底栏查看是否允许弹框（仅限已接受协议的JP/KR卖家）
  const allowViewMode = React.useMemo(() => {
    // content 可能包含 is_jp_kr_user 和 has_accepted_terms 字段
    if (!isViewMode) return true; // 非viewMode不限制
    if (!content) return false; // 未加载内容不弹框
    // 只有JP/KR卖家且已接受协议才允许弹框，否则走/articles/terms
    if (content.is_jp_kr_user && content.has_accepted_terms) {
      return true;
    }
    return false;
  }, [isViewMode, content]);

  if (!isOpen || (isViewMode && !allowViewMode)) {
    // 禁止底栏查看弹框（非JP/KR或未接受协议）
    if (typeof document !== 'undefined' && document.body.hasAttribute('data-modal-scroll-disabled')) {
      document.body.style.overflow = '';
      document.body.removeAttribute('data-modal-scroll-disabled');
    }
    return null;
  }
  console.log('T&C Modal: Rendering modal', { isOpen, viewMode });
  // 生产环境移除页面标题调试代码

  return (
    <>

      {isViewMode ? (
        // 浮层模态框 - 没有背景遮罩（仅用于底栏查看条款）
        <div
          className="terms-modal-container view-mode"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.1)',
            width: '900px',  // 进一步增大宽度，确保长标题一行显示
            height: 'auto',
            maxWidth: '96vw',
            maxHeight: '90vh',  // 稍微增加高度
            minWidth: '800px',
            minHeight: 'auto',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 2147483647,  // 最高z-index，确保在背景遮罩之上
            pointerEvents: 'auto',
            overflow: 'auto',
            boxSizing: 'border-box',
            border: '1px solid #e1e5e9'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          {showCloseButton && onClose && (
            <button
              className="terms-modal-close"
              onClick={onClose}
              type="button"
              aria-label="Close"
            >
              ×
            </button>
          )}

          {/* 配置文件中的content已包含完整HTML结构，包括terms-modal-content */}
          {loading ? (
            <div className="terms-modal-content">
              <div className="terms-loading">
                <div>Loading Terms and Conditions...</div>
              </div>
            </div>
          ) : content ? (
            <>
              {/* 在view mode下使用过滤后的内容，移除复选框和按钮 */}
              <div
                dangerouslySetInnerHTML={{ __html: getFilteredContent(content.content) }}
                onClick={handleContentClick}
              />
            </>
          ) : (
            <div className="terms-modal-content">
              <div className="terms-error">
                <div>Failed to load Terms and Conditions</div>
                <button onClick={fetchContent} type="button">Retry</button>
              </div>
            </div>
          )}
        </div>
      ) : (
        // 模态框 - 接受模式需要背景遮罩，阻止其他操作
        <>
          {/* 全屏背景遮罩 - 完全阻止与底层页面交互 */}
          <div
            className="terms-modal-backdrop"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2147483646,  // 最高z-index，确保覆盖一切
              cursor: 'not-allowed',
              userSelect: 'none',
              pointerEvents: 'auto'
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }}
          />
          <div
            className="terms-modal-container acceptance-mode"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'white',
              borderRadius: '8px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.1)',
              width: '900px',  // 增大宽度，确保标题一行显示且便于阅读
              height: 'auto',
              maxWidth: '96vw',
              maxHeight: '90vh',  // 增大最大高度
              minWidth: '800px',
              minHeight: 'auto',  // 改为自动高度
              display: 'flex',
              flexDirection: 'column',
              zIndex: 1000,
              pointerEvents: 'auto',
              overflow: 'auto',
              boxSizing: 'border-box',
              border: '1px solid #e1e5e9'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            {showCloseButton && onClose && (
              <button
                className="terms-modal-close"
                onClick={onClose}
                type="button"
                aria-label="Close"
              >
                ×
              </button>
            )}

          {/* 配置文件中的content已包含完整HTML结构，包括terms-modal-content */}
          {loading ? (
            <div className="terms-modal-content">
              <div className="terms-loading">
                <div>Loading Terms and Conditions...</div>
              </div>
            </div>
          ) : content ? (
            <>
              {/* 直接使用配置文件中的完整HTML结构 */}
              <div
                dangerouslySetInnerHTML={{ __html: content.content }}
                onClick={handleContentClick}
              />
            </>
          ) : (
            <div className="terms-modal-content">
              <div className="terms-error">
                <div>Failed to load Terms and Conditions</div>
                <button onClick={fetchContent} type="button">Retry</button>
              </div>
            </div>
          )}
        </div>
        </>
      )}

      {/* Inline Styles - Only render when modal is open */}
      {isOpen && <style>{`
        /* 查看模式样式 - 适中尺寸浮层效果，便于阅读 */
        .terms-modal-container.view-mode {
          position: fixed !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          background: white !important;
          border-radius: 8px !important;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.1) !important;
          display: flex !important;
          flex-direction: column !important;
          z-index: 2147483647 !important;
          pointer-events: auto !important;
          overflow: auto !important;
          box-sizing: border-box !important;
          border: 1px solid #e1e5e9 !important;
          animation: slideInFloat 0.3s ease-out !important;
          /* 优化尺寸，确保标题一行显示，便于内容阅读 */
          width: 900px !important;
          height: auto !important;
          max-width: 96vw !important;
          max-height: 90vh !important;
          min-width: 800px !important;
          min-height: auto !important;
        }

        /* 接受模式的背景遮罩 - 阻止所有交互 */
        .terms-modal-backdrop {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          width: 100vw !important;
          height: 100vh !important;
          background-color: rgba(0, 0, 0, 0.5) !important;
          z-index: 2147483646 !important;
          pointer-events: auto !important;
          user-select: none !important;
          cursor: not-allowed !important;
        }

        .terms-modal-container.acceptance-mode {
          /* 接受模式也改为小尺寸浮层效果 */
          position: fixed !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          background: white !important;
          border-radius: 8px !important;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.1) !important;
          width: 900px !important;
          height: auto !important;
          max-width: 96vw !important;
          max-height: 90vh !important;
          min-width: 800px !important;
          min-height: auto !important;
          display: flex !important;
          flex-direction: column !important;
          z-index: 2147483647 !important;
          pointer-events: auto !important;
          overflow: auto !important;
          box-sizing: border-box !important;
          border: 1px solid #e1e5e9 !important;
          animation: slideInFloat 0.3s ease-out !important;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideInFloat {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }

        @keyframes slideInModal {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .terms-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #666;
          z-index: 1000001;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
        }

        .terms-modal-close:hover {
          background-color: #f0f0f0;
          color: #333;
        }

        .terms-modal-content {
          padding: 24px;
          display: flex;
          flex-direction: column;
          height: auto;
          min-height: 0;
          max-height: 70vh; /* 增加内容区域高度 */
          overflow-y: auto;
          flex-grow: 1;
        }

        .terms-loading,
        .terms-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          text-align: center;
          gap: 16px;
        }

        .terms-header {
          border-bottom: 1px solid #e1e5e9;
          padding-bottom: 16px;
          margin-bottom: 16px;
        }

        .terms-header h2 {
          margin: 0;
          font-size: 24px;
          font-weight: bold;
          color: #333;
        }

        .terms-body {
          flex: 1;
          overflow-y: auto;
          margin-bottom: 16px;
          min-height: auto; /* 让内容自适应 */
          max-height: 60vh; /* 限制最大高度，避免过高 */
          touch-action: pan-y;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }

        .terms-text {
          background-color: #f8f9fa;
          border: 1px solid #e1e5e9;
          border-radius: 6px;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          line-height: 1.6;
          color: #333;
        }

        .terms-text p {
          margin: 0 0 12px 0;
        }

        .terms-text ul {
          margin: 12px 0;
          padding-left: 20px;
        }

        .terms-text li {
          margin-bottom: 6px;
        }

        .terms-text a {
          color: #2255ef;
          font-weight: 600;
          text-decoration: underline;
        }

        .terms-text a:hover {
          color: #1a40b8;
        }

        /* 配置文件中使用的额外CSS类 */
        .terms-links {
          margin: 16px 0;
          padding: 16px;
          background-color: #f8f9fa;
          border-left: 4px solid #2255ef;
          border-radius: 4px;
        }

        /* 确保terms-links内的所有p和a标签没有垂直间距 */
        .terms-links p,
        .terms-links a {
          margin: 0 !important;
          padding: 0 !important;
        }

        /* 重新为terms-link-label添加必要的样式 */
        .terms-links .terms-link-label {
          font-weight: 600 !important;
          color: #333 !important;
          font-size: 14px !important;
          margin: 0 !important;
        }

        /* 为有margin-top inline style的链接标签提供更高优先级 */
        .terms-link-label[style*="margin-top"] {
          margin-top: 16px !important;
        }

        .terms-subtitle {
          font-size: 16px;
          font-weight: 600;
          color: #333;
          margin: 20px 0 12px 0;
          padding-bottom: 8px;
          border-bottom: 2px solid #e1e5e9;
        }

        .terms-note {
          font-size: 12px;
          color: #666;
          font-style: italic;
          margin: 8px 0;
        }

        .terms-list {
          margin: 12px 0;
          padding-left: 20px;
        }

        .terms-list li {
          margin-bottom: 8px;
          line-height: 1.6;
        }

        .terms-checkbox {
          padding: 16px;
          border-top: 1px solid #e1e5e9;
          border-bottom: 1px solid #e1e5e9;
          background-color: #f8f9fa;
          margin: 16px 0;
        }

        .terms-checkbox label {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          cursor: pointer;
          font-size: 14px;
          color: #333;
          font-weight: 500;
          line-height: 1.6;
        }

        .terms-checkbox input[type="checkbox"] {
          margin-top: 4px;
          width: 18px;
          height: 18px;
          cursor: pointer;
          flex-shrink: 0;
        }

        .terms-footer {
          display: flex;
          justify-content: center;
          gap: 16px;
          margin-top: 16px;
        }

        .terms-accept-btn,
        .terms-details-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 6px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          min-width: 120px;
        }

        .terms-accept-btn {
          background-color: #28a745;
          color: white;
        }

        .terms-accept-btn:hover:not(:disabled) {
          background-color: #218838;
        }

        .terms-accept-btn:disabled {
          background-color: #ccc;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .terms-details-btn {
          background-color: #6c757d;
          color: white;
        }

        .terms-details-btn:hover {
          background-color: #545b62;
        }

        .terms-error button {
          background-color: #007bff;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
        }

        .terms-error button:hover {
          background-color: #0056b3;
        }

        @media (max-width: 768px), (max-height: 600px) {
          .terms-modal-container.view-mode {
            width: calc(100vw - 40px) !important;
            height: auto !important;
            max-width: calc(100vw - 40px) !important;
            max-height: calc(100vh - 80px) !important;
            min-height: auto !important;
            min-width: 300px !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
          }

          .terms-modal-backdrop {
            display: none !important; /* 移动设备上也隐藏背景遮罩 */
          }

          .terms-modal-container.acceptance-mode {
            /* 移动设备上也保持小尺寸浮层效果 */
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            width: auto !important;
            height: auto !important;
            max-width: calc(100vw - 20px) !important;
            max-height: calc(100vh - 40px) !important;
            min-width: 320px !important;
            min-height: auto !important;
          }

          .terms-modal-content {
            padding: 16px;
          }

          .terms-header h2 {
            font-size: 18px;
            line-height: 1.4;
          }

          .terms-body {
            min-height: auto;
            max-height: 50vh;
          }

          .terms-text {
            font-size: 13px;
            padding: 15px;
          }

          .terms-accept-btn,
          .terms-details-btn {
            padding: 12px 16px;
            font-size: 14px;
            min-width: 100px;
            width: 100%;
          }

          .terms-footer {
            flex-direction: column;
            gap: 12px;
            margin-top: 12px;
          }

          .terms-checkbox {
            margin: 12px 0;
            padding: 12px;
          }

          .terms-checkbox label {
            font-size: 13px;
            gap: 8px;
          }
        }

        @media (max-height: 600px) {
          .terms-modal-container {
            max-height: calc(100vh - 20px);
            min-height: auto;
          }

          .terms-body {
            min-height: auto;
            max-height: 40vh;
          }
        }

        /* 强制覆盖任何可能的样式冲突 */
        .terms-modal-container * {
          box-sizing: border-box !important;
        }

        /* 确保查看模式浮层始终居中显示 */
        @media screen {
          .terms-modal-container.view-mode {
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            z-index: 2147483647 !important;
          }

          /* 确保没有背景遮罩 */
          .terms-modal-backdrop {
            display: none !important;
          }

          .terms-modal-container.acceptance-mode {
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            z-index: 2147483647 !important;
          }
        }

        /* 强制覆盖样式 - 最高优先级，优化尺寸 */
        body .terms-modal-container.view-mode {
          position: fixed !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          z-index: 2147483647 !important;
          width: 900px !important;
          height: auto !important;
          max-width: 96vw !important;
          max-height: 90vh !important;
        }

        /* View模式下隐藏复选框和按钮 */
        .terms-modal-container.view-mode .terms-checkbox,
        .terms-modal-container.view-mode .terms-footer,
        .terms-modal-container.view-mode .terms-accept-btn,
        .terms-modal-container.view-mode .terms-details-btn,
        .terms-modal-container.view-mode input[type="checkbox"] {
          display: none !important;
          visibility: hidden !important;
        }

        /* 完全移除背景遮罩 */
        body .terms-modal-backdrop {
          display: none !important;
        }

        /* 确保接受模式也是浮层效果 */
        body .terms-modal-backdrop .terms-modal-container.acceptance-mode,
        body .terms-modal-container.acceptance-mode {
          position: fixed !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          z-index: 2147483647 !important;
        }
      `}</style>}
    </>
  );
};

export default TermsAndConditionsModal;
